import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, Add, Download, Description } from "@mui/icons-material";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetResourceDocumentsQuery, useDeleteResourceDocumentMutation } from "./api/resourceDocumentsApi";
import { ResourceDocument } from "./types/resourceDocument";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditResourceDocument from "./components/AddEditResourceDocument";

const ResourceDocumentManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ResourceDocument | null>(null);

  const { data: resourceDocuments, isLoading, isError, isFetching } = useGetResourceDocumentsQuery();
  const [deleteResourceDocument] = useDeleteResourceDocumentMutation();

  const columns: Array<MRT_ColumnDef<ResourceDocument>> = [
    {
      header: "Title",
      accessorKey: "title",
      size: 250,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.title || 'Untitled Document'}
          </Typography>
        );
      },
    },
    {
      header: "Category",
      accessorKey: "category",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Chip
            label={original.category || 'Uncategorized'}
            size="small"
            variant="outlined"
            color="primary"
          />
        );
      },
    },
    {
      header: "Type",
      accessorKey: "type",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Chip
            label={original.type || 'Policy'}
            size="small"
            variant="outlined"
            color="secondary"
          />
        );
      },
    },
    {
      header: "Format",
      accessorKey: "file_format",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Description fontSize="small" color="action" />
            <Typography variant="body2">
              {original.file_format || 'PDF'}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "File Size",
      accessorKey: "file_size",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" color="text.secondary">
            {original.file_size || 'N/A'}
          </Typography>
        );
      },
    },
    {
      header: "Version",
      accessorKey: "version",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" color="text.secondary">
            {original.version || 'N/A'}
          </Typography>
        );
      },
    },
    {
      header: "Author",
      accessorKey: "author",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" color="text.secondary">
            {original.author || 'N/A'}
          </Typography>
        );
      },
    },
    {
      header: "Downloads",
      accessorKey: "downloads",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" color="text.secondary">
            {original.downloads || 0}
          </Typography>
        );
      },
    },
    {
      header: "Featured",
      accessorKey: "is_featured",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Chip
            label={original.is_featured ? 'Yes' : 'No'}
            size="small"
            color={original.is_featured ? 'success' : 'default'}
            variant={original.is_featured ? 'filled' : 'outlined'}
          />
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedDocument) {
      try {
        await deleteResourceDocument(selectedDocument.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedDocument(null);
      } catch (error) {
        console.error("Failed to delete resource document:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedDocument(null);
  };

  return (
    <Box>
      <DataTable<ResourceDocument>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Resource Documents</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${resourceDocuments?.data?.length ?? 0} Documents`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedDocument(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
              startIcon={<Add />}
            >
              Add New Document
            </Button>
          </Box>
        }
        list={resourceDocuments?.data ?? []}
        count={resourceDocuments?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            {row.file_url && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.file_url, '_blank');
                }}
                color="primary"
                title="Download"
              >
                <Download />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDocument(row);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDocument(row);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <AddEditResourceDocument
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        resourceDocument={selectedDocument}
      />

      {selectedDocument && (
        <ConfirmModal
          type="warning"
          title="Delete Resource Document"
          message={`Are you sure you want to delete '${selectedDocument.title}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={handleDelete}
        />
      )}
    </Box>
  );
};

export default ResourceDocumentManager;


